// src/pages/blog/BlogDetailPage.js
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { fetchArticle } from '../../services/api';
import { useLang } from '../../i18n/LanguageContext';
import { LoadingSpinner, ErrorBox, StarRating } from '../../components/ui';
import styles from './BlogDetailPage.module.css';

function formatDate(dateStr, lang) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  } catch { return dateStr; }
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const { t, ui, lang } = useLang();
  const { data: article, isLoading, isError, refetch } = useQuery(
    ['article', slug],
    () => fetchArticle(slug),
    { staleTime: 1000 * 60 * 5 }
  );

  if (isLoading) return <div style={{ paddingTop: 'calc(var(--nav-height) + 4rem)' }}><LoadingSpinner /></div>;
  if (isError) return <div style={{ paddingTop: 'calc(var(--nav-height) + 4rem)' }}><ErrorBox onRetry={refetch} /></div>;
  if (!article) return null;

  return (
    <main className={styles.main}>
      <Link to="/blog" className={styles.back}>
        {ui('← بازگشت به مقالات', '← Back to Blog')}
      </Link>

      <article className={styles.article}>
        {/* Cover */}
        <div className={styles.cover}>
          {article.image
            ? <img src={article.image} alt={t(article, 'title')} className={styles.coverImg} />
            : <span className={styles.coverEmoji}>{article.cover_emoji || '🕯️'}</span>
          }
        </div>

        {/* Meta */}
        <div className={styles.meta}>
          {article.category && (
            <span className={styles.catBadge} style={{ background: article.category.color }}>
              {t(article.category, 'name')}
            </span>
          )}
          <span className={styles.date}>{formatDate(article.published_at, lang)}</span>
          <span className={styles.readTime}>
            {article.read_time_minutes} {ui('دقیقه مطالعه', 'min read')}
          </span>
          <span className={styles.views}>👁 {article.view_count}</span>
        </div>

        <h1 className={`${styles.title} grad-text`}>{t(article, 'title')}</h1>
        <p className={styles.excerpt}>{t(article, 'excerpt')}</p>

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className={styles.tags}>
            {article.tags.map((tag) => (
              <span key={tag.id} className={styles.tag}>{t(tag, 'name')}</span>
            ))}
          </div>
        )}

        {/* Body */}
        <div className={styles.body}>
          {t(article, 'body').split('\n').map((para, i) =>
            para.trim() ? <p key={i}>{para}</p> : <br key={i} />
          )}
        </div>

        {/* CTA */}
        <div className={styles.cta}>
          <p className={styles.ctaText}>
            {ui('برای سفارش شمع‌های دست‌ساز با ما در تماس باشید:', 'To order handmade candles, contact us:')}
          </p>
          <div className={styles.ctaBtns}>
            <a href="https://t.me/cutecandels" target="_blank" rel="noreferrer" className="btn-primary">
              📨 {ui('تلگرام', 'Telegram')}
            </a>
            <a href="https://eitaa.com/cutecandels" target="_blank" rel="noreferrer" className="btn-secondary">
              📱 {ui('ایتا', 'Eitaa')}
            </a>
          </div>
        </div>
      </article>
    </main>
  );
}
